import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileSignature, FileSpreadsheet, Loader2, Plus } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { ContractForm } from "@/components/contracts/ContractForm";
import { ApproversDialog } from "@/components/contracts/Approvers";
import { AlertTriangle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AllPayments } from "@/components/contracts/AllPayments";
import { fmtTL, fmtDate } from "@/lib/utils";

export default function Contracts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const load = () => api.get("/contracts").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    ref.current.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/contracts/import", fd, { timeout: 120000 });
      toast.success(`${r.data.firm}: ${r.data.payments} hakediş, ${r.data.items} kalem, ${r.data.advances} avans aktarıldı`);
      navigate(`/sozlesmeler/${r.data.contract_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Aktarım başarısız"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="contracts-page">
      <PageHeader title="Hakediş" subtitle="Alt yüklenici sözleşmeleri, hakediş raporu, onay, ödeme ve avans takibi.">
        <input type="file" accept=".xlsx,.xlsm" ref={ref} className="hidden" onChange={onFile} data-testid="contract-import-input" />
        <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid="contract-import-button">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-600" />} Excel Hakediş Aktar
        </Button>
        <ApproversDialog />
        <Button onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="new-contract-button"><Plus className="w-4 h-4 mr-1" /> Yeni Sözleşme</Button>
      </PageHeader>
      <ContractForm open={open} onOpenChange={setOpen} onSaved={(c) => navigate(`/sozlesmeler/${c.id}`)} />
      <Tabs defaultValue="contracts">
      <TabsList className="mb-4"><TabsTrigger value="contracts" data-testid="tab-contracts">Sözleşmeler</TabsTrigger><TabsTrigger value="all" data-testid="tab-all-payments">Tüm Hakedişler</TabsTrigger></TabsList>
      <TabsContent value="all"><AllPayments /></TabsContent>
      <TabsContent value="contracts">
      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center" data-testid="contracts-empty">
          <FileSignature className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz sözleşme yok</div>
          <div className="text-sm text-muted-foreground mt-1">Mevcut hakediş Excel'inizi aktarın veya yeni sözleşme oluşturun.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map(c => (
            <button key={c.id} onClick={() => navigate(`/sozlesmeler/${c.id}`)} className="text-left rounded-xl border border-border bg-card p-5 hover:border-amber-500 transition-colors" data-testid={`contract-card-${c.id}`}>
              <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground font-mono">{c.contract_no || "—"}</span>
                {c.warnings?.length > 0 && <span className="flex items-center gap-1 text-[11px] font-semibold text-rose-600" data-testid={`contract-warn-${c.id}`}><AlertTriangle className="w-3.5 h-3.5" /> {c.warnings.length} uyarı</span>}</div>
              <div className="font-display font-bold mt-1 leading-snug">{c.firm_name}</div>
              <div className="text-sm text-muted-foreground">{c.subject}</div>
              <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${Math.min(c.progress || 0, 100)}%` }} /></div>
              <div className="flex justify-between text-xs mt-2">
                <span>Kümülatif: <strong className="font-mono-nums">{fmtTL(c.cumulative)}</strong></span>
                <span className="text-muted-foreground">%{c.progress ?? "-"} / {fmtTL(c.amount)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-2">{c.payment_count} hakediş{c.last_payment && ` · son: ${c.last_payment.label || c.last_payment.no} (${fmtDate(c.last_payment.date)})`}</div>
            </button>
          ))}
        </div>
      )}
      </TabsContent>
      </Tabs>
    </div>
  );
}
